import { useCallback, useState } from "react";
import type { StoredBenchmarkRun } from "./types";

const STORAGE_KEY = "BENCHMARK_RUNS";
const MAX_STORED_RUNS = 50;

function readRuns(): StoredBenchmarkRun[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredBenchmarkRun[]) : [];
  } catch {
    return [];
  }
}

function writeRuns(runs: StoredBenchmarkRun[]): void {
  try {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(runs));
    }
  } catch {
    console.error("Failed to write benchmark runs to localStorage");
  }
}

export function useBenchmarkHistory() {
  const [runs, setRuns] = useState<StoredBenchmarkRun[]>(readRuns);

  const saveRun = useCallback((run: StoredBenchmarkRun) => {
    setRuns((prev) => {
      const next = [run, ...prev].slice(0, MAX_STORED_RUNS);
      writeRuns(next);
      return next;
    });
  }, []);

  const deleteRun = useCallback((id: string) => {
    setRuns((prev) => {
      const next = prev.filter((r) => r.id !== id);
      writeRuns(next);
      return next;
    });
  }, []);

  return { runs, saveRun, deleteRun };
}
